import { useMemo } from 'react'
import { useLocalStorageState } from './useLocalStorageState.ts'

export type ChartMode = 'sun' | 'tree'
export type ChartDepthByMode = Record<ChartMode, number>

const STORAGE_KEY = 'dashboard-chart-preferences'
const DEFAULT_MODE: ChartMode = 'tree'
const DEFAULT_DEPTH_BY_MODE: ChartDepthByMode = {
  sun: 3,
  tree: 4,
}

type StoredPreferences = {
  chartMode: ChartMode
  chartDepthByMode: ChartDepthByMode
}

function isChartMode(value: unknown): value is ChartMode {
  return value === 'sun' || value === 'tree'
}

function sanitizeDepth(value: unknown, fallback: number) {
  const num = Number(value)

  if (!Number.isInteger(num)) return fallback

  return Math.min(6, Math.max(1, num))
}

function deserializePreferences(raw: string): StoredPreferences {
  const parsed = JSON.parse(raw) as Partial<StoredPreferences> | null

  return {
    chartMode: isChartMode(parsed?.chartMode) ? parsed.chartMode : DEFAULT_MODE,
    chartDepthByMode: {
      sun: sanitizeDepth(parsed?.chartDepthByMode?.sun, DEFAULT_DEPTH_BY_MODE.sun),
      tree: sanitizeDepth(parsed?.chartDepthByMode?.tree, DEFAULT_DEPTH_BY_MODE.tree),
    },
  }
}

export function useDashboardChartPreferences() {
  const [preferences, setPreferences] = useLocalStorageState<StoredPreferences>(
    STORAGE_KEY,
    {
      chartMode: DEFAULT_MODE,
      chartDepthByMode: DEFAULT_DEPTH_BY_MODE,
    },
    {
      deserialize: deserializePreferences,
    },
  )

  const activeLeafDepth = preferences.chartDepthByMode[preferences.chartMode]

  return useMemo(
    () => ({
      chartMode: preferences.chartMode,
      chartDepthByMode: preferences.chartDepthByMode,
      activeLeafDepth,
      setChartMode: (nextMode: ChartMode) => {
        setPreferences((current) => ({
          ...current,
          chartMode: nextMode,
        }))
      },
      setChartDepth: (mode: ChartMode, nextDepth: number) => {
        setPreferences((current) => ({
          ...current,
          chartDepthByMode: {
            ...current.chartDepthByMode,
            [mode]: sanitizeDepth(nextDepth, current.chartDepthByMode[mode]),
          },
        }))
      },
    }),
    [activeLeafDepth, preferences.chartDepthByMode, preferences.chartMode, setPreferences],
  )
}
